import { useColorScheme } from '@mui/material/styles';
import { useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

/**
 * Single shared dark/light mode hook. Wraps MUI's built-in useColorScheme()
 * (already the source of truth for MUI-styled pages) so custom-styled pages
 * (heatmap, chart platform, tabs, etc.) can read/write the exact same state
 * instead of maintaining their own local darkMode toggle.
 *
 * Persists to the backend (/api/settings -> dark_mode_enabled) so the
 * preference follows the user across devices/browsers, not just this one
 * browser's localStorage (which is all MUI does on its own).
 */
export function useDarkMode() {
  const { mode, systemMode, setMode } = useColorScheme();
  // 'system' follows the OS (MUI resolves it to systemMode); unknown/undefined still reads as dark, as before.
  const darkMode = (mode === 'system' ? systemMode : mode) !== 'light';

  const setDarkMode = useCallback((value) => {
    setMode(value ? 'dark' : 'light');
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ dark_mode_enabled: value }),
    }).catch(() => {});
  }, [setMode]);

  const toggleDarkMode = useCallback(() => setDarkMode(!darkMode), [darkMode, setDarkMode]);

  return { darkMode, setDarkMode, toggleDarkMode };
}
